import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useSlate } from 'slate-react';
import ReactDataSheet from 'libs/react-datasheet';
import { insertMention } from 'slate/extensions/mentions';
import { Spinner } from 'app/components';
import { SearchInput } from 'app/components/ui';
import { useEditorDocument, useEditorLocale, useEditorReferencesQuery } from 'app/api/documents/useEditorData';
import { localizedCellValue } from 'shared/lib/cell-locale';
import { ReferenceCell, ReferenceDataEntry, ReferenceDataValue } from 'entities/document';
import { TablePickerGrid } from './TablePickerGrid';

interface CellSelection {
  start: { i: number; j: number };
  end?: { i: number; j: number };
}

interface ImportReferenceProps {
  onClose: (...args: unknown[]) => void;
}

export const ImportReference = ({ onClose }: ImportReferenceProps) => {
  const { t } = useTranslation('table-import');

  const [state, setState] = useState('sheet');
  const [search, setSearch] = useState('');
  const [selection, setSelection] = useState<CellSelection | null>(null);
  const [sheet, setSheet] = useState<ReferenceDataValue | undefined>();
  const states = ['sheet', 'cell'];
  const editor = useSlate();
  const { data: editorDocument } = useEditorDocument();
  const locale = useEditorLocale();

  const { references, isLoading } = useEditorReferencesQuery() as { references: ReferenceDataValue[]; isLoading: boolean };

  const handleReferenceCellSelection = (reference: ReferenceDataValue) => {
    setSheet(reference);
    setState(states[1]);
  };

  const handleCellSelection = (reference: ReferenceDataValue, selection: CellSelection) => {
    if (!reference.revisionId) {
      return;
    }
    insertMention(editor, { ...reference, revisionId: reference.revisionId }, selection);
    onClose();
    setState(states[0]);
    setSelection(null);
  };

  const sheetLocale = editorDocument?.content?.referenceData?.find((entry: ReferenceDataEntry) => entry.superId === sheet?.superId)?.locale;
  const valueRenderer = (cell: ReferenceCell) => localizedCellValue(cell, locale, sheetLocale === locale);

  const switcher = (page: string) => {
    switch (page) {
      case states[0]:
        return isLoading ? <Spinner /> : <TablePickerGrid references={references} search={search} onConfirm={handleReferenceCellSelection} />;
      case states[1]:
        return (
          <div className="select--cell">
            <div className="select--cell-header">
              <button
                className="btn btn-secondary btn-sm back-to-tables"
                onClick={() => {
                  setState(states[0]);
                  setSelection(null);
                }}
              >
                <i className="fa-regular fa-chevron-left" /> {t('back-to-tables')}
              </button>
              <span className="select--cell-name">{sheet?.name}</span>
            </div>
            <ReactDataSheet
              data={sheet?.selectionContent}
              valueRenderer={valueRenderer}
              overflow="clip"
              readOnly
              onSelect={(selection: CellSelection) => {
                setSelection(selection);
              }}
            />
            <div className="image-footer">
              {!selection ? null : (
                <>
                  <h3 className="selected-cell-label">
                    {t('insert-value', { cell: `[${selection.start.i}, ${selection.start.j}]` })}:{' '}
                    {valueRenderer(sheet?.selectionContent?.[selection.start.i]?.[selection.start.j] ?? {})}
                  </h3>
                  <button
                    className="btn btn-secondary"
                    onClick={(e) => {
                      e.persist();
                      onClose();
                    }}
                  >
                    {t('cancel')}
                  </button>
                  <button className="btn btn-primary" onClick={() => sheet && handleCellSelection(sheet, selection)}>
                    {t('approve')}
                  </button>
                </>
              )}
            </div>
          </div>
        );
      default:
        return <div className="box error">Unknown state</div>;
    }
  };

  return (
    <div>
      <div className="header d-flex">
        <h3>{t('include-reference')}</h3>
        {state === states[0] ? (
          <SearchInput className="table-picker-search" searchInput={search} setSearchInput={setSearch} placeholder={t('search-placeholder')} />
        ) : null}
      </div>
      <div className="body">{switcher(state)}</div>
    </div>
  );
};
